import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import { useCompanyInfo, usePageContent } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function OmHovendalsPage() {
  const companyInfo = useCompanyInfo();
  const page = usePageContent('om-hovendals', {
    badge: "Vores historie & værdier",
    heading: "Om Hovendals",
    text: "Udvikling og vækst siden 2019 – klejnsmed, byg og montage med fokus på kvalitetshåndværk og kundetilfredshed.",
    image: "/_assets/thumbnail-1000003444.jpg",
    metaTitle: "Hovendals » Om os | Du kan lære mere om os og vores værdier her",
    metaDescription: "Vi udfører opgaver inden for ✓ klejnsmed, ✓ byg og montage, ✓ fliensrens og ✓ ejendomsservice. Lærs mere om, hvem vi er her →",
  });

  return (
    <>
      <SEO
        title={page.metaTitle || "Hovendals » Om os | Du kan lære mere om os og vores værdier her"}
        description={page.metaDescription || "Vi udfører opgaver inden for ✓ klejnsmed, ✓ byg og montage, ✓ fliensrens og ✓ ejendomsservice. Lærs mere om, hvem vi er her →"}
        ogImage={page.image || "/_assets/thumbnail-1000003444.jpg"}
      />

      {/* Hero */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl(page.image || "/_assets/thumbnail-1000003444.jpg", "width=1600,quality=80,format=auto")}
            alt="Om Hovendals"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#004fa4]/20 text-[#c2ccdf] border border-[#004fa4]/30">
              <i className="fa-sharp fa-solid fa-user-check" aria-hidden="true"></i>
              <span>{page.badge || "Vores historie & værdier"}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {page.heading || "Om Hovendals"}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
              {page.text || "Udvikling og vækst siden 2019 – klejnsmed, byg og montage med fokus på kvalitetshåndværk og kundetilfredshed."}
            </p>
          </div>
        </div>
      </section>

      {/* Values Banner */}
      <section className="bg-[#143463] text-white py-6 border-y border-[#183E7F]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3">
              <i className="fa-solid fa-star text-xl text-[#C2CCDF]" aria-hidden="true"></i>
              <span className="text-base font-medium">Vi går op i kundetilfredshed</span>
            </div>
            <div className="flex items-center justify-center md:justify-start gap-3">
              <i className="fa-sharp fa-solid fa-shield-check text-xl text-[#C2CCDF]" aria-hidden="true"></i>
              <span className="text-base font-medium">Vi er kvalitetsbevidste - hver gang</span>
            </div>
            <div className="flex items-center justify-center md:justify-start gap-3">
              <i className="fa-sharp fa-solid fa-handshake text-xl text-[#C2CCDF]" aria-hidden="true"></i>
              <span className="text-base font-medium">Vi overholder aftaler</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Udvikling og vækst siden 2019</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                  Fra enkeltmandsvirksomhed til et stærkt fagligt team
                </h2>
              </div>
              <p className="text-slate-700 leading-relaxed text-base">
                Firmaet <Link to="/" className="text-[#004fa4] font-bold hover:underline">Hovendals</Link> bliver stiftet den 1. Oktober 2019 af Simon Hovendal. Firmaet har været under en lang udvikling siden da, der i dag tilbyder mange forskellige vedligeholdelsesfrie løsninger. Efter at have kørt virksomheden Hovendals som enkeltmands virksomhed, blev Hovendals ApS stiftet den 26. Oktober 2022. Virksomheden fortsætter sin fremgang.
              </p>
              <p className="text-slate-700 leading-relaxed text-base">
                Hovendals sætter en ære i at have en tæt dialog med jer både før, under og efter opgaven. På den måde sikrer vi jer en høj kvalitet og en kundetilfredshed, der er helt i top. Vi er nemlig fast besluttet på, at opgaven først er færdig, når du er helt tilfreds. Det, mener vi, er den eneste måde at afslutte et projekt på.
              </p>

              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="px-6 py-3 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all"
                >
                  Start dit projekt
                </Link>
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                  <span>Ring {companyInfo.phone}</span>
                </a>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-slate-100">
              <img
                src={imageUrl("/_assets/thumbnail-1000003444.jpg", "width=800,quality=85,format=auto")}
                alt="Om Hovendals"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Hovendals udfører */}
          <div className="pt-8 border-t border-slate-100">
            <div className="mb-10 text-center max-w-2xl mx-auto">
              <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Hovendals udfører:
              </h3>
              <p className="text-slate-600 text-sm mt-2">
                Faglig stolthed, solide materialer og løsninger tilpasset dine ønsker.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="w-12 h-12 rounded-xl bg-[#004fa4]/10 text-[#004fa4] flex items-center justify-center text-xl font-bold mb-4">
                  <i className="fa-sharp fa-solid fa-paint-roller" aria-hidden="true"></i>
                </div>
                <h4 className="text-lg font-bold text-slate-900">
                  Renovering & opfriskning
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Vi hjælper dig med at give dit hjem eller din bygning nyt liv gennem renovering og opfriskning af facader. Med fokus på både funktion og æstetik sikrer vi, at resultatet bliver flot og langtidsholdbart.
                </p>
              </div>

              <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="w-12 h-12 rounded-xl bg-[#004fa4]/10 text-[#004fa4] flex items-center justify-center text-xl font-bold mb-4">
                  <i className="fa-sharp fa-solid fa-temperature-arrow-up" aria-hidden="true"></i>
                </div>
                <h4 className="text-lg font-bold text-slate-900">
                  Isolerede facader
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  En isoleret facade er en investering i både komfort og energibesparelse. Med den rette løsning kan du mindske varmetab betydeligt og dermed reducere dine energiomkostninger år efter år. Hos Hovendals tilbyder vi facadeløsninger med høj isoleringsværdi, der ikke kun giver et sundere og mere behageligt indeklima, men også bidrager til en mere bæredygtig hverdag.
                </p>
              </div>

              <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="w-12 h-12 rounded-xl bg-[#004fa4]/10 text-[#004fa4] flex items-center justify-center text-xl font-bold mb-4">
                  <i className="fa-sharp fa-solid fa-compass-drafting" aria-hidden="true"></i>
                </div>
                <h4 className="text-lg font-bold text-slate-900">
                  Specialdesignede løsninger
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Har du særlige ønsker til udtryk, materialer eller funktioner? Vi designer og skræddersyr løsninger, så de matcher både bygningens arkitektur og dine behov. På den måde får du en unik facade, der skiller sig ud og holder i mange år.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <QuoteSection />
    </>
  );
}
